<script setup lang="ts">
interface Props {
  url: string
  alt?: string
  caption?: string
  variant: 'cover_aside' | 'cover_full_width'
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'cover_aside',
})

const isCoverAside = computed(() => props.variant === 'cover_aside')

const isImageFull = computed(() => props.variant === 'cover_full_width')

const ratio = computed(() => {
  if (isCoverAside.value) {
    return 16 / 9
  }

  return 16 / 7
})

const maxWidth = computed(() => {
  if (isCoverAside.value) {
    return 1024
  }

  return 1248
})

const { getTwicpicsCoverHref, getFixedWidthsSet, getResponsiveWidthsSet, toSrcsetString } = useLCP({ imagePath: props.url, ratio })

// fixed rule
const fixedWidths = getFixedWidthsSet(maxWidth.value)
const fixedSrcSet = toSrcsetString(fixedWidths)

// responsive rule
const responsiveWidths = getResponsiveWidthsSet(maxWidth.value)
const maxResponsiveWidth = Math.max(...responsiveWidths)
const responsiveSrcSet = toSrcsetString(new Set<number>([...responsiveWidths, ...[...fixedWidths].filter(w => w < maxResponsiveWidth)]))

const unionSrcSet = toSrcsetString(new Set<number>([...responsiveWidths, ...fixedWidths]))

const responsiveHref = getTwicpicsCoverHref({ width: 320 })
const fixedHref = getTwicpicsCoverHref({ width: maxWidth.value })

useHead({
  link: [
    {
      rel: 'preload',
      as: 'image',
      media: `(max-width: ${maxWidth.value}px)`,
      href: responsiveHref,
      imagesrcset: responsiveSrcSet,
      imagesizes: '100vw',
      fetchpriority: 'high',
    },
    {
      rel: 'preload',
      as: 'image',
      media: `(min-width: ${maxWidth.value + 1}px)`,
      href: fixedHref,
      imagesrcset: fixedSrcSet,
      imagesizes: `${maxWidth.value}px`,
      fetchpriority: 'high',
    },
  ],
})
</script>

<template>
  <div class="container">
    <figure
      class="mt-4 space-y-1.5 -mx-4 sm:mx-auto"
      :class="{ 'max-w-screen-lg ': isCoverAside }"
    >
      <img
        :src="responsiveHref"
        :srcset="unionSrcSet"
        :sizes="`
            (max-width: ${maxWidth}px) 100vw,
            ${maxWidth}px
          `"
        fetchpriority="high"
        :alt="props.alt"
        :style="`aspect-ratio: ${ratio};`"
        class="object-cover w-full sm:rounded"
      >
      <figcaption
        v-if="props.caption && isImageFull"
        class="px-1 text-sm italic text-gray-500"
      >
        {{ props.caption }}
      </figcaption>
    </figure>
  </div>
</template>
